<template>
  <div class="calendar-selector" style="margin-bottom: 7px;">
    <h3>{{ displayMonth }}</h3>
    <hr>
    <div class="days-of-week">
      <div>su</div>
      <div>mo</div>
      <div>tu</div>
      <div>we</div>
      <div>th</div>
      <div>fr</div>
      <div>sa</div>
    </div>
    <div class="dates">
      <div v-for="(week, weekKey) in weeks" :key="weekKey" class="week">
        <div v-for="(date, dateKey) in week" :key="dateKey" class="date">
          <label
            :for="`date-${dateKey}-${weekKey}`"
            :class="{'active': date.available, 'checked': rescheduleData.date_to_deliver === date.date, 'disabled': !date.available}"
          >
            {{ getDateNumber(date.date) }}
          </label>
          <input
            :id="`date-${dateKey}-${weekKey}`"
            v-model="rescheduleData.date_to_deliver"
            type="radio"
            :value="date.date"
            :disabled="!date.available"
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { commUrl, isRx4Route } from '@/config'
import { sameDayAddressDate } from '@/utils/trackingSameDay.mjs'
import axios from 'axios'
import { mapGetters } from 'vuex'

export default {
  name: 'calendar-selector',
  props: {
    changeAddress: { type: Boolean, default: false },
  },
  data() {
    return {
      holidays: [],
      availableDays: [],
      displayDays: [],
      weeks: [],
      loaded: false,
    }
  },
  computed: {
    ...mapGetters(['rescheduleData', 'orderData', 'isTrackingRescheduleLimited', 'trackingReschedulePolicy']),
    sameDayDate() {
      return sameDayAddressDate(this.orderData, this.changeAddress, isRx4Route)
    },
    displayMonth() {
      if (!this.displayDays.length) return moment().format('MMMM')

      const firstDate = moment(this.displayDays[0])
      const lastDate = moment(this.displayDays[this.displayDays.length - 1])

      if (firstDate.month() !== lastDate.month()) {
        return `${firstDate.format('MMMM')} - ${lastDate.format('MMMM')}`
      }

      return firstDate.format('MMMM')
    },
  },
  watch: {
    sameDayDate() {
      if (this.loaded) {
        this.getAvailableDays()
        this.calculateWeeks()
      }
    },
  },
  async mounted() {
    moment.locale('en-us')
    const res = await axios.get(`${commUrl}get-holidays`)
    this.holidays = res.data.holidays
    this.loaded = true

    this.getAvailableDays()
    this.calculateWeeks()
  },
  methods: {
    getDateNumber(date) {
      return moment(date).format('DD')
    },
    getAvailableDays() {
      if (this.isTrackingRescheduleLimited) {
        this.availableDays = this.trackingReschedulePolicy?.allowed_dates ?? []
        this.displayDays = (this.trackingReschedulePolicy?.candidate_dates ?? []).length
          ? this.trackingReschedulePolicy.candidate_dates
          : this.availableDays

        this.$store.commit('setRescheduleDataParse', { date_to_deliver: this.availableDays[0] ?? null })
        return
      }

      const today = isRx4Route && this.orderData.same_day_address_change?.today
        ? moment(this.orderData.same_day_address_change.today)
        : moment().startOf('day')
      today.add(1, 'days')
      this.availableDays = []
      this.displayDays = []

      if (this.sameDayDate) {
        this.availableDays.push(this.sameDayDate)
        this.displayDays.push(this.sameDayDate)
      }

      for (let foundDays = 0; foundDays < 5; today.add(1, 'days')) {
        if (today.day() !== 0 && !this.holidays.includes(today.format('YYYY-MM-DD'))) {
          this.availableDays.push(today.format('YYYY-MM-DD'))
          this.displayDays.push(today.format('YYYY-MM-DD'))
          foundDays++
        }
      }

      if (this.availableDays.length > 0) {
        this.$store.commit('setRescheduleDataParse', { date_to_deliver: this.availableDays[0] })
      }
    },
    getWeekFromDate(date) {
      return moment(date).isoWeek()
    },
    calculateWeeks() {
      this.weeks = []

      if (this.displayDays.length === 0) {
        return
      }

      if (this.isTrackingRescheduleLimited) {
        const startWeek = moment(this.displayDays[0]).startOf('week')
        const endWeek = moment(this.displayDays[this.displayDays.length - 1]).endOf('week')

        const currentWeekStart = startWeek.clone()
        while (currentWeekStart.isSameOrBefore(endWeek)) {
          const weekEnd = currentWeekStart.clone().endOf('week')
          this.weeks.push(this.addDays(currentWeekStart, weekEnd))
          currentWeekStart.add(1, 'week')
        }
        return
      }

      let currentWeekStart = moment(this.availableDays[0]).startOf('week')

      this.availableDays.forEach(day => {
        const dayMoment = moment(day)
        if (!dayMoment.isSame(currentWeekStart, 'week')) {
          currentWeekStart = dayMoment.startOf('week')
        }
        if (!this.weeks.find(w => moment(w[0].date).isSame(currentWeekStart, 'week'))) {
          const weekEnd = currentWeekStart.clone().endOf('week')
          this.weeks.push(this.addDays(currentWeekStart, weekEnd))
        }
      })
    },
    addDays(start, end) {
      const days = []
      for (let d = moment(start); d.isSameOrBefore(end); d.add(1, 'days')) {
        let available = this.availableDays.includes(d.format('YYYY-MM-DD'))
        if (!this.isTrackingRescheduleLimited && d.day() === 0 && d.format('YYYY-MM-DD') !== this.sameDayDate) {
          available = false
        }

        days.push({
          date: d.format('YYYY-MM-DD'),
          available,
        })
      }
      return days
    },
  },
}
</script>
